// =============================================================================
// AdminBrandingPage — email branding settings (Branding tab)
// =============================================================================
// Edits the singleton EmailBrandingSettings row that drives the header/footer
// of every branded email (logo, brand color, company identity, legal links,
// CAN-SPAM postal address, support contact). The From *address* is intentionally
// NOT editable here — it must stay on the verified Resend sending domain and is
// configured via env. A live preview renders the resolved header/footer chrome.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasPermission } from '@tkpod/shared';
import { Button, Card, CardHeader, Input, Spinner } from '@tkpod/ui';

import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import { toast } from '@/lib/toast';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BrandingSettings {
  logoUrl: string | null;
  websiteUrl: string | null;
  brandColor: string | null;
  fromName: string | null;
  replyTo: string | null;
  companyName: string | null;
  postalAddress: string | null;
  supportEmail: string | null;
  privacyUrl: string | null;
  termsUrl: string | null;
  headerHtml: string | null;
  footerNote: string | null;
  updatedAt?: string;
}

interface FormState {
  logoUrl: string;
  websiteUrl: string;
  brandColor: string;
  fromName: string;
  replyTo: string;
  companyName: string;
  postalAddress: string;
  supportEmail: string;
  privacyUrl: string;
  termsUrl: string;
  headerHtml: string;
  footerNote: string;
}

const DEFAULT_BRAND_COLOR = '#111827';

const EMPTY_FORM: FormState = {
  logoUrl: '',
  websiteUrl: '',
  brandColor: DEFAULT_BRAND_COLOR,
  fromName: '',
  replyTo: '',
  companyName: '',
  postalAddress: '',
  supportEmail: '',
  privacyUrl: '',
  termsUrl: '',
  headerHtml: '',
  footerNote: '',
};

const queryKeys = { branding: ['admin', 'notification-branding'] as const };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function settingsToForm(s: BrandingSettings | null): FormState {
  if (!s) return EMPTY_FORM;
  return {
    logoUrl: s.logoUrl ?? '',
    websiteUrl: s.websiteUrl ?? '',
    brandColor: s.brandColor ?? DEFAULT_BRAND_COLOR,
    fromName: s.fromName ?? '',
    replyTo: s.replyTo ?? '',
    companyName: s.companyName ?? '',
    postalAddress: s.postalAddress ?? '',
    supportEmail: s.supportEmail ?? '',
    privacyUrl: s.privacyUrl ?? '',
    termsUrl: s.termsUrl ?? '',
    headerHtml: s.headerHtml ?? '',
    footerNote: s.footerNote ?? '',
  };
}

/** Empty optional strings become `null`; URLs/emails sent only when non-blank. */
function buildPayload(form: FormState): Record<string, unknown> {
  const opt = (v: string) => {
    const t = v.trim();
    return t === '' ? null : t;
  };
  return {
    logoUrl: opt(form.logoUrl) ?? undefined,
    websiteUrl: opt(form.websiteUrl) ?? undefined,
    brandColor: form.brandColor.trim() || DEFAULT_BRAND_COLOR,
    fromName: opt(form.fromName),
    replyTo: opt(form.replyTo),
    companyName: opt(form.companyName),
    postalAddress: opt(form.postalAddress),
    supportEmail: opt(form.supportEmail),
    privacyUrl: opt(form.privacyUrl) ?? undefined,
    termsUrl: opt(form.termsUrl) ?? undefined,
    headerHtml: opt(form.headerHtml),
    footerNote: opt(form.footerNote),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminBrandingPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canManage = hasPermission(user?.permissions ?? [], 'notification:manage');

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const settingsQuery = useQuery<BrandingSettings | null, Error>({
    queryKey: queryKeys.branding,
    queryFn: () =>
      api.get<BrandingSettings | null>('/api/v1/admin/notifications/branding'),
  });

  // Seed the form once the settings load (and on refetch).
  useEffect(() => {
    if (settingsQuery.data !== undefined) {
      setForm(settingsToForm(settingsQuery.data));
    }
  }, [settingsQuery.data]);

  const saveMutation = useMutation<BrandingSettings, Error, void>({
    mutationFn: () =>
      api.put<BrandingSettings>(
        '/api/v1/admin/notifications/branding',
        buildPayload(form),
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.branding, data);
      toast('Branding saved.');
    },
    onError: (err) => setError(err.message),
  });

  const field = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSave = useCallback(() => {
    setError(null);
    if (form.logoUrl.trim() && !/^https?:\/\//i.test(form.logoUrl.trim())) {
      setError('Logo URL must start with http:// or https://');
      return;
    }
    if (form.websiteUrl.trim() && !/^https?:\/\//i.test(form.websiteUrl.trim())) {
      setError('Website URL must start with http:// or https://');
      return;
    }
    saveMutation.mutate();
  }, [form, saveMutation]);

  if (settingsQuery.isLoading && !settingsQuery.data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading branding..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader
              title="Email branding"
              description="Controls the header & footer of every branded email. The From address stays on the verified sending domain (configured via env)."
            />

            {error && (
              <div className="mt-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
                {error}
              </div>
            )}

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Logo URL"
                value={form.logoUrl}
                onChange={(e) => field('logoUrl', e.target.value)}
                placeholder="https://cdn.example.com/logo.png"
                disabled={!canManage}
              />
              <Input
                label="Website URL"
                value={form.websiteUrl}
                onChange={(e) => field('websiteUrl', e.target.value)}
                placeholder="https://transferkingdompod.com"
                hint="Header logo & company name link here."
                disabled={!canManage}
              />
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Brand color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.brandColor}
                    onChange={(e) => field('brandColor', e.target.value)}
                    disabled={!canManage}
                    className="h-9 w-12 shrink-0 cursor-pointer rounded border border-border bg-surface disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <Input
                    aria-label="Brand color hex"
                    value={form.brandColor}
                    onChange={(e) => field('brandColor', e.target.value)}
                    disabled={!canManage}
                    placeholder="#111827"
                  />
                </div>
              </div>

              <Input
                label="From name"
                value={form.fromName}
                onChange={(e) => field('fromName', e.target.value)}
                placeholder="Transfer Kingdom"
                disabled={!canManage}
              />
              <Input
                label="Reply-to address"
                value={form.replyTo}
                onChange={(e) => field('replyTo', e.target.value)}
                placeholder="support@transferkingdompod.com"
                disabled={!canManage}
              />

              <Input
                label="Company name"
                value={form.companyName}
                onChange={(e) => field('companyName', e.target.value)}
                disabled={!canManage}
              />
              <Input
                label="Support email"
                value={form.supportEmail}
                onChange={(e) => field('supportEmail', e.target.value)}
                disabled={!canManage}
              />

              <Input
                label="Privacy policy URL"
                value={form.privacyUrl}
                onChange={(e) => field('privacyUrl', e.target.value)}
                placeholder="https://…/privacy"
                disabled={!canManage}
              />
              <Input
                label="Terms URL"
                value={form.termsUrl}
                onChange={(e) => field('termsUrl', e.target.value)}
                placeholder="https://…/terms"
                disabled={!canManage}
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                Postal address (CAN-SPAM)
              </label>
              <textarea
                value={form.postalAddress}
                onChange={(e) => field('postalAddress', e.target.value)}
                rows={2}
                maxLength={500}
                disabled={!canManage}
                className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 disabled:opacity-50"
                placeholder="123 Print St, Suite 100, City, State ZIP, Country"
              />
              <p className="mt-1 text-xs text-text-tertiary">
                A physical mailing address is legally required in the footer of
                marketing email.
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                Footer note
              </label>
              <textarea
                value={form.footerNote}
                onChange={(e) => field('footerNote', e.target.value)}
                rows={2}
                maxLength={1000}
                disabled={!canManage}
                className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 disabled:opacity-50"
                placeholder="You're receiving this because you have a Transfer Kingdom account."
              />
            </div>

            {canManage && (
              <div className="mt-6 flex justify-end">
                <Button onClick={handleSave} loading={saveMutation.isPending}>
                  Save branding
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Live preview of header/footer chrome */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader title="Preview" description="Header & footer chrome." />
            <div className="mt-4 overflow-hidden rounded-lg border border-border">
              <div
                className="flex items-center justify-center gap-3 px-4 py-4"
                style={{ backgroundColor: form.brandColor || DEFAULT_BRAND_COLOR }}
              >
                {(() => {
                  const mark = form.logoUrl ? (
                    <img
                      src={form.logoUrl}
                      alt="Logo"
                      className="h-14 max-w-[220px] object-contain"
                    />
                  ) : (
                    <span className="text-sm font-semibold text-white">
                      {form.companyName || form.fromName || 'Transfer Kingdom'}
                    </span>
                  );
                  return form.websiteUrl ? (
                    <a
                      href={form.websiteUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="no-underline"
                    >
                      {mark}
                    </a>
                  ) : (
                    mark
                  );
                })()}
              </div>
              <div className="bg-white px-4 py-8 text-center text-sm text-gray-500">
                Email body
              </div>
              <div className="border-t border-border bg-gray-50 px-4 py-4 text-center text-[11px] leading-relaxed text-gray-500">
                {form.footerNote && <p>{form.footerNote}</p>}
                <p className="mt-1">
                  {form.companyName || 'Transfer Kingdom'}
                  {form.postalAddress ? ` · ${form.postalAddress}` : ''}
                </p>
                <p className="mt-1 space-x-2">
                  {form.privacyUrl && <span className="underline">Privacy</span>}
                  {form.termsUrl && <span className="underline">Terms</span>}
                  <span className="underline">Unsubscribe</span>
                </p>
              </div>
            </div>
            <p className="mt-3 text-xs text-text-tertiary">
              Marketing emails always include an unsubscribe link and your postal
              address. Transactional emails omit the unsubscribe link.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
